import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePlanner } from "../../contexts/PlannerContext";
import { useStorage } from "../../contexts/StorageContext";

const FILE_PATH = "batches/mudoBatches.json";

export default function BatchView() {
  const navigate = useNavigate();
  const { batchId } = useParams();
  const { events = [], upsertEvents } = usePlanner();
  const { hasFolder, loadJSON } = useStorage();

  const [batch, setBatch] = useState(null);
  const [legacyTasks, setLegacyTasks] = useState([]);

  // Load the batch (folder if available, else LS)
  useEffect(() => {
    let alive = true;
    (async () => {
      let list = [];
      if (hasFolder) {
        try { list = await loadJSON(FILE_PATH, []); } catch {}
      }
      if (!Array.isArray(list) || list.length === 0) {
        try { list = JSON.parse(localStorage.getItem("mudoBatches") || "[]"); } catch { list = []; }
      }

      if (!alive) return;
      const found = (list || []).find((b) => String(b.id) === String(batchId)) || null;
      if (found) {
        setBatch({
          ...found,
          status: found.status === "Planned" ? "Not Started" : (found.status || "Not Started"),
        });
      } else {
        setBatch(null);
      }
    })();

    return () => { alive = false; };
  }, [batchId, hasFolder, loadJSON]);

  // Planner-backed tasks for this batch
  const plannerTasks = useMemo(
    () => (events || []).filter((e) => String(e.batchId) === String(batchId)),
    [events, batchId]
  );

  // Legacy fallback: localStorage mudoTasks
  useEffect(() => {
    try {
      const all = JSON.parse(localStorage.getItem("mudoTasks") || "[]");
      setLegacyTasks((all || []).filter((t) => String(t.batchId) === String(batchId)));
    } catch {
      setLegacyTasks([]);
    }
  }, [batchId]);

  const tasksForView = plannerTasks.length ? plannerTasks : legacyTasks;

  // One-click migration (legacy -> planner)
  const sendLegacyToPlanner = () => {
    if (!legacyTasks.length) return alert("No legacy tasks found for this batch.");

    const toStartEnd = (dateStr) => {
      const d = new Date(dateStr);
      const y = d.getFullYear(), m = d.getMonth(), day = d.getDate();
      return {
        start: new Date(y, m, day, 9, 0, 0, 0).toISOString(),
        end:   new Date(y, m, day, 17, 0, 0, 0).toISOString(),
      };
    };
    const stageTagFor = (name = "") => {
      const n = name.toLowerCase();
      if (n.includes("glaze firing")) return "glazeFiring";
      if (n.includes("bisque"))       return "bisque";
      if (n.includes("glazing"))      return "glazing";
      if (n.includes("dry"))          return "drying";
      if (n.includes("finish"))       return "qcPack";
      if (n.includes("form") || n.includes("prepare")) return "forming";
      return undefined;
    };

    const payload = legacyTasks.map((t, i) => {
      const { start, end } = toStartEnd(t.startDate);
      const tag = stageTagFor(t.category);
      return {
        id: t.id || `${batchId}-${i}`,
        title: t.title || "Batch Task",
        start, end,
        status: "planned",
        progress: 0,
        qty: 1,
        category: "production",
        taskType: t.category || undefined,
        tags: tag ? [tag] : undefined,
        batch: batch?.name,
        batchId: batchId,
        productId: undefined,
        source: "batch",
        notes: "",
      };
    });

  upsertEvents(payload);
  alert("Tasks sent to Planner");
  };

  if (!batch) {
    return (
      <div className="p-6 text-white">
        <button
          onClick={() => navigate("/batches")}
          className="px-3 py-2 rounded bg-zinc-700 hover:bg-zinc-600 mb-4"
        >
          Back to Batches
        </button>
        <div className="text-sm opacity-70">Batch not found.</div>
      </div>
    );
  }

  const displayStatus = (s) => (s === "Planned" ? "Not Started" : (s || "Not Started"));

  return (
    <div className="p-6 text-white space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate("/batches")}
          className="px-3 py-2 rounded bg-zinc-700 hover:bg-zinc-600"
        >
          ← Back to Batches
        </button>
        <button
          onClick={() => navigate("/dashboard")}
          className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700"
        >
          Dashboard
        </button>
        <button className="px-3 py-2 rounded bg-orange-600 hover:bg-orange-700">
          Export PDF
        </button>
        <button className="px-3 py-2 rounded bg-indigo-600 hover:bg-indigo-700">
          Print
        </button>
        {!plannerTasks.length && legacyTasks.length > 0 && (
          <button
            onClick={sendLegacyToPlanner}
            className="ml-auto px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-700"
          >
            Send to Planner
          </button>
        )}
      </div>

      {/* Meta */}
      <div>
        <h1 className="text-xl font-bold">
          Batch: <span className="opacity-90">{batch.name}</span>
        </h1>
        <div className="text-sm opacity-80 mt-1">
          Products: {batch.products?.length || 0} | Start Date: {batch.startDate || "—"}
        </div>
        <div className="text-sm opacity-80">Status: {displayStatus(batch.status)}</div>
        <div className="text-sm opacity-80">
          Total Cost: ${Number(batch.totalCost || 0).toFixed(2)}
        </div>
      </div>

      {/* Tasks */}
      <section className="space-y-2">
  <div className="font-semibold">Tasks</div>
        {tasksForView.length === 0 ? (
          <div className="text-sm opacity-70">No tasks found for this batch.</div>
        ) : (
          <div className="rounded border border-white/10 divide-y divide-white/10">
            {tasksForView.map((t) => {
              const start = t.start ?? (t.startDate ? new Date(t.startDate).toISOString() : null);
              const date = start ? new Date(start).toISOString().slice(0, 10) : (t.startDate || "—");
              return (
                <div key={t.id} className="px-3 py-2 text-sm flex items-center justify-between">
                  <div className="truncate">
                    <span className="opacity-90">{t.title}</span>
                    <span className="opacity-70"> — {date}</span>
                    {t.taskType && <span className="opacity-60"> • {t.taskType}</span>}
                  </div>
                  <div className="opacity-70">{t.status || "todo"}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Kiln placeholder */}
      <section className="space-y-2">
  <div className="font-semibold">Kiln Firings</div>
        <div className="text-sm opacity-70">No firings linked to this batch.</div>
      </section>
    </div>
  );
}
